import { NavLink } from 'react-router-dom';
import { Home, Activity, Settings, Folder, Plus, HardDrive } from 'lucide-react';
import clsx from 'clsx';
import { Workspace } from '../../types';

interface SidebarProps {
  workspaces?: Workspace[];
  currentWorkspace?: string;
  onCreateWorkspace?: () => void;
}

export function Sidebar({ workspaces = [], currentWorkspace, onCreateWorkspace }: SidebarProps) {
  return (
    <aside className="w-64 bg-white border-r flex flex-col h-full">
      {/* New Workspace Button - Like Google Drive's "New" button */}
      <div className="p-4">
        <button
          onClick={onCreateWorkspace}
          className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-gray-700 font-medium"
        >
          <Plus className="w-5 h-5 text-primary-600" />
          New Workspace
        </button>
      </div>

      <nav className="flex-1 px-3 overflow-y-auto">
        {/* Main Navigation */}
        <ul className="space-y-1">
          <li>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-full text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <Home className="w-5 h-5" />
              Home
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/activity"
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-full text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <Activity className="w-5 h-5" />
              Activity
            </NavLink>
          </li>
        </ul>

        {/* Workspaces Section */}
        {workspaces.length > 0 && (
          <div className="mt-6">
            <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Workspaces
            </h3>
            <ul className="space-y-1">
              {workspaces.map((ws) => (
                <li key={ws.name}>
                  <NavLink
                    to={`/workspace/${ws.name}`}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors',
                        isActive || currentWorkspace === ws.name
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    <HardDrive className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{ws.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* Bottom Settings */}
      <div className="p-3 border-t">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors',
              isActive
                ? 'bg-primary-100 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            )
          }
        >
          <Settings className="w-5 h-5" />
          Settings
        </NavLink>
      </div>
    </aside>
  );
}
